import { useEffect, useRef, useState, type ReactNode } from "react";
import { SiteLink, useRouter } from "../router";

const primaryNavigation = [
  { label: "교수", href: "/about" },
  { label: "구성원", href: "/people", match: ["/people", "/alumni"] },
  { label: "연구", href: "/research", match: ["/research", "/projects"] },
  { label: "출판 기록", href: "/publications" },
  { label: "활동", href: "/news" },
  { label: "연락처", href: "/contact" }
];

export function AppShell({ children }: { children: ReactNode }) {
  const { path } = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);

  useEffect(() => setMenuOpen(false), [path]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        menuButton.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  return (
    <div className="site">
      <a className="skip-link" href="#main">
        본문으로 건너뛰기
      </a>
      <header className="site-header">
        <div className="shell header-inner">
          <SiteLink to="/" className="brand" aria-current={path === "/" ? "page" : undefined}>
            <span className="brand-ko">최정혜 교수 연구실</span>
            <span className="brand-en">Yonsei · Marketing</span>
          </SiteLink>
          <nav className="desktop-nav" aria-label="주요 메뉴">
            {primaryNavigation.map((item) => {
              const active = item.match?.includes(path) ?? item.href === path;
              return (
                <SiteLink
                  key={item.href}
                  to={item.href}
                  className={active ? "nav-link is-active" : "nav-link"}
                  aria-current={active ? "page" : undefined}
                >
                  {item.label}
                </SiteLink>
              );
            })}
          </nav>
          <button
            ref={menuButton}
            className="menu-button"
            type="button"
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span>{menuOpen ? "닫기" : "메뉴"}</span>
            <span className="menu-glyph" aria-hidden="true">
              {menuOpen ? "×" : "≡"}
            </span>
          </button>
        </div>
        <nav
          id="mobile-navigation"
          className={menuOpen ? "mobile-nav is-open" : "mobile-nav"}
          aria-label="모바일 메뉴"
          hidden={!menuOpen}
        >
          <div className="shell mobile-nav-inner">
            {primaryNavigation.map((item, index) => (
              <SiteLink
                key={item.href}
                to={item.href}
                className="mobile-nav-link"
                onNavigate={() => setMenuOpen(false)}
              >
                <span>{String(index + 1).padStart(2, "0")}</span>
                {item.label}
              </SiteLink>
            ))}
          </div>
        </nav>
      </header>
      <main id="main" tabIndex={-1}>
        {children}
      </main>
      <SiteFooter />
    </div>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="shell footer-grid">
        <div>
          <p className="footer-brand">최정혜 교수 연구실</p>
          <p>Yonsei School of Business · Marketing</p>
        </div>
        <div>
          <p>서울특별시 서대문구 연세로 50</p>
          <p>경영관 537호 · +82 2 2123 6575</p>
        </div>
        <div>
          <a href="mailto:jeonghye@yonsei.ac.kr">jeonghye@yonsei.ac.kr</a>
          <SiteLink to="/contact">자료 출처와 이용 안내</SiteLink>
        </div>
      </div>
      <div className="shell footer-bottom">
        <span>© {new Date().getFullYear()} Jeonghye Choi Research Group</span>
        <span>Preview · Noindex</span>
      </div>
    </footer>
  );
}

export function PageIntro({
  label,
  title,
  description,
  aside
}: {
  label: string;
  title: string;
  description?: string;
  aside?: ReactNode;
}) {
  return (
    <header className="page-intro shell">
      <div className="page-intro-copy">
        <p className="section-label">{label}</p>
        <h1 tabIndex={-1}>{title}</h1>
        {description ? <p className="page-description">{description}</p> : null}
      </div>
      {aside ? <div className="page-intro-aside">{aside}</div> : null}
    </header>
  );
}

export function SectionHeader({
  label,
  title,
  action
}: {
  label?: string;
  title: string;
  action?: ReactNode;
}) {
  return (
    <div className="section-header">
      <div>
        {label ? <p className="section-label">{label}</p> : null}
        <h2>{title}</h2>
      </div>
      {action ? <div className="section-action">{action}</div> : null}
    </div>
  );
}

export function ArrowLink({
  to,
  children
}: {
  to: string;
  children: ReactNode;
}) {
  return (
    <SiteLink to={to} className="arrow-link">
      <span>{children}</span>
      <span aria-hidden="true">↗</span>
    </SiteLink>
  );
}

export function ExternalLink({
  href,
  children
}: {
  href: string;
  children: ReactNode;
}) {
  return (
    <a className="external-link" href={href} target="_blank" rel="noreferrer">
      {children}
      <span aria-hidden="true">↗</span>
    </a>
  );
}
